import React from 'react';
import { Building2, CheckCircle2, Clock, Loader2, X } from 'lucide-react';

/**
 * Two popups for the "add a company from the chat" flow.
 *
 * The assistant never adds a company on its own - a chat message that looks
 * like "add Tesla" comes back as a proposal, and this modal is where the user
 * actually agrees to it. That matters because confirming starts a multi-minute
 * collection sweep that spends real search and LLM quota.
 */
export function ConfirmCompanyModal({ action, busy, error, onConfirm, onCancel }) {
  if (!action) return null;

  const isRefetch = action.type === 'refetch_company';
  const title = isRefetch
    ? `Re-run collection for ${action.company}?`
    : `Add ${action.company} to your companies?`;
  const confirmLabel = isRefetch ? 'Re-run collection' : 'Add & start collection';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-slate-900/45" onClick={onCancel} aria-label="Close" />
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-[#e1e5ec] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e1e5ec] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[#e7edfc] text-[#1c4ed8] flex items-center justify-center">
              <Building2 size={20} />
            </div>
            <h2 className="text-base font-semibold text-[#0d1524]">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-2 rounded-lg text-[#76829a] hover:text-[#45516a] hover:bg-[#f6f7fa] transition"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="flex gap-3 text-sm text-[#45516a] bg-[#f6f7fa] border border-[#e1e5ec] rounded-md px-4 py-3">
            <Clock size={16} className="text-[#76829a] flex-shrink-0 mt-0.5" />
            <p>
              {isRefetch ? (
                <>
                  A fresh sweep takes <span className="font-semibold text-[#0d1524]">a couple of minutes</span> — it
                  re-checks legal, regulatory, HR, criminal and financial news.
                </>
              ) : (
                <>
                  Fetching a new company takes up to <span className="font-semibold text-[#0d1524]">3-4 minutes</span>
                  {' '}— it searches legal, regulatory, HR, criminal and financial news.
                </>
              )}
              {' '}You&apos;ll get a notification here as soon as the results are ready.
            </p>
          </div>

          {error && (
            <p className="text-sm text-[#bd2f2b] bg-[#fae8e7] border border-[#f1c9c7] rounded-lg px-3 py-2">{error}</p>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="flex-1 px-3 text-[13px] font-medium text-[#45516a] border border-[#e1e5ec] rounded-md hover:bg-[#f6f7fa] transition disabled:opacity-50 h-8"
            >
              Not now
            </button>
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="flex-1 flex items-center justify-center gap-2 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] border border-transparent rounded-md hover:brightness-110 transition disabled:opacity-50 h-8"
            >
              {busy ? (<><Loader2 size={16} className="animate-spin" /> Starting…</>) : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function CompanyReadyModal({ result, onView, onDismiss }) {
  if (!result) return null;

  const count = Number(result.records) || 0;
  const message = result.timedOut
    ? 'The collection is taking longer than expected. It is still running in the background — check back shortly.'
    : count > 0
      ? `Collected ${count} new record${count === 1 ? '' : 's'}. The answer is waiting in your chat.`
      : 'The sweep finished but found nothing new in the trusted sources for this window.';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button type="button" className="absolute inset-0 bg-slate-900/45" onClick={onDismiss} aria-label="Close" />
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl border border-[#e1e5ec] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#e1e5ec] flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#e1f3ea] text-[#117049] flex items-center justify-center">
            <CheckCircle2 size={20} />
          </div>
          <h2 className="text-base font-semibold text-[#0d1524]">
            Results ready for {result.company}
          </h2>
        </div>

        <div className="p-5 space-y-4">
          <p className="text-sm text-[#45516a]">{message}</p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onDismiss}
              className="flex-1 px-3 text-[13px] font-medium text-[#45516a] border border-[#e1e5ec] rounded-md hover:bg-[#f6f7fa] transition h-8"
            >
              Dismiss
            </button>
            <button
              type="button"
              onClick={onView}
              className="flex-1 px-3 text-[13px] font-medium text-white bg-[#1c4ed8] border border-transparent rounded-md hover:brightness-110 transition h-8"
            >
              Show me the news
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
